import { siteApiSlice } from "../../app/siteApiSlice";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { DataTablesQueryModel, DataTablesViewModel } from "../../interfaces/dataTables";

export interface ManageCourseWritersViewModel {
	showExpirationWarning: boolean;
	expirationDate: Date;
	allowChanges: boolean;
	seatsRemaining: number;
	limitedSeatLicenses: boolean;
	noSeatLicenses: boolean;
	writersPage: StudentsPage;
	editModel: ManageCourseWritersEditModel;
	allowNewStudentCreation: boolean;
	showSeatsRemaining: boolean;
}

export interface CourseWriterRow {
	id: number;
	userName: string;
	password: string;
	lName: string;
	fName: string;
	fullName: string;
	formalFullName: string;
	lastLoginDate: Date;
	hasImage: boolean;
	initials: string;
	isCleverUser: boolean;
	isClassLinkUser: boolean;
}

export interface StudentsPage {
	items: CourseWriterRow[];
	totalCount: number;
	currentPage: number;
	pageSize: number;
	numPages: number;
}

export interface ManageCourseWritersEditModel {
	nameFilter: string;
}

export interface ManageCourseQueryModel {
	nameFilter: string;
	page?: number;
}

export interface CourseInfoViewModel {
	courseCode: string;
	grade: string;
	studentCount: number;
	isCleverCourse: boolean;
	isClassLinkCourse: boolean;
	coteachers: string[];
}

export interface AvailableStudentsViewModel {
	dT_RowId: string;
	name: string;
	username: string;
}

export interface AvailableStudentsQueryModel extends DataTablesQueryModel {
	level_Filter: string;
	search_Filter: string;
}

export interface RemoveStudentViewModel {
	fullName: string;
	username: string;
}

export interface AddExistingTeacherViewModel {
	courseId: number;
	userId: number;
}

export interface ProctorCourseEditModel {
	name: string;
	beginDate: Date;
	endDate: Date;
	isDateRangeRequired: boolean;
	canProctorEditCourseGrade: boolean;
	gradeId: number;
	isCleverCourse: boolean;
	isClassLinkCourse: boolean;
	disableUserImages: boolean;
	disableUserImagesByDistrict: boolean;
	disableUserImagesBySchool: boolean;
}

export const manageCourseApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		manage: build.query<ManageCourseWritersViewModel, ManageCourseQueryModel>({
			query: (q) => {
				const { nameFilter, page } = q;
				return {
					url: `/CourseWriters/Manage`,
					params: { nameFilter, page },
				};
			},
			providesTags: ["ManageCourse", "CurrentCourse"],
		}),
		getCourseInfo: build.query<CourseInfoViewModel, void>({
			query: () => ({
				url: `/CourseWriters/GetCourseInfo`,
			}),
			providesTags: ["ManageCourse", "CurrentCourse"],
		}),
		getAvailableStudents: build.query<
			DataTablesViewModel<AvailableStudentsViewModel>,
			AvailableStudentsQueryModel
		>({
			query: (q) => {
				const {
					iSortCol_0,
					sSortDir_0,
					iDisplayStart,
					iDisplayLength,
					level_Filter,
					search_Filter,
				} = q;
				return {
					url: `/CourseWriters/AvailableExistingStudentsAjaxHandler`,
					params: {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						level_Filter,
						search_Filter,
					},
				};
			},
			providesTags: ["ManageCourse", "CurrentCourse"],
		}),
		addStudents: build.mutation<string[], number[]>({
			query: (rowIds) => ({
				url: `/CourseWriters/AddExistingStudentsToCourse`,
				method: "POST",
				body: { studentIds: rowIds },
			}),
			invalidatesTags: [
				"ManageCourse",
				"PeerReviewGroup",
				"LessonHistory",
				"WritingHistory",
				"PeerReview",
			],
		}),
		removeStudents: build.mutation<RemoveStudentViewModel, number>({
			query: (studentId) => ({
				url: `/CourseWriters/Remove`,
				method: "POST",
				body: { id: studentId },
			}),
			invalidatesTags: [
				"ManageCourse",
				"PeerReviewGroup",
				"LessonHistory",
				"WritingHistory",
				"Prompts",
				"PeerReview",
			],
		}),
		availableTeachers: build.query<SimpleOption[], number>({
			query: (courseId) => ({
				url: `/Course/AvailableTeachers`,
				params: { courseId },
			}),
			providesTags: ["ManageCourse", "CurrentCourse", "CourseInfo"],
		}),
		addExistingTeacher: build.mutation<void, AddExistingTeacherViewModel>({
			query: (q) => {
				const { courseId, userId } = q;
				return {
					url: `/Course/AddExistingProctor`,
					method: "POST",
					body: { courseId, userId },
				};
			},
			invalidatesTags: ["ManageCourse", "CourseInfo", "UserInfo"],
		}),
		getEditCourseByProctorInfo: build.query<ProctorCourseEditModel, void>({
			query: () => ({
				url: `/Course/EditCourseByProctor`,
			}),
			providesTags: ["ManageCourse", "CurrentCourse"],
		}),
		editCourseByProctor: build.mutation<void, ProctorCourseEditModel>({
			query: (payload) => {
				return {
					url: `/Course/EditCourseByProctor`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["ManageCourse", "CurrentCourse"],
		}),
		getAllStudentsForPrinting: build.query<CourseWriterRow[], ManageCourseWritersEditModel>({
			query: (filter) => {
				return {
					url: `/CourseWriters/GetAllWritersForPrinting`,
					method: "POST",
					body: { filter },
				};
			},
		}),
	}),
});

export const {
	useManageQuery,
	useLazyManageQuery,
	useGetCourseInfoQuery,
	useGetAvailableStudentsQuery,
	useLazyGetAvailableStudentsQuery,
	useAddStudentsMutation,
	useRemoveStudentsMutation,
	useAvailableTeachersQuery,
	useAddExistingTeacherMutation,
	useGetEditCourseByProctorInfoQuery,
	useEditCourseByProctorMutation,
	useLazyGetAllStudentsForPrintingQuery,
} = manageCourseApiSlice;
